import { useQuery } from "react-query";
import TableOrganism from "../../organisms/Table/TableOrganism";
import { createHeaderRow } from "../../utilities/CreateHeaderRow";
import { createNotificationsRows, NotificationData } from "./CreateNotificationsTable";
import { TableRowProps } from "../../molecules/TableRow/TableRowAtom";
import { useLoaderDispatch, useLoaderState } from "../../context/LoaderContext";
import { startLoading, stopLoading } from "../../context/actions/loaderActions";
import Loader from "../../atoms/Loader/LoaderAtom";
import useApiService from "../../utilities/useApiService";
import { UserRole } from "../../utilities/Enums";
import { useAuthState } from "../../context/AuthContext";
import { useEffect, useMemo, useState } from "react";
import { Pagination } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { useNotificationCount } from "../../context/NotificationContext";
import ModalWithText from "../../molecules/ModalWithText/ModalWithText";
import { formatLocalTime } from "../../utilities/DataMatrixParser/formatTime";
import { OFFLINE_DEVICE_APPROVAL_PENDING_LABEL } from "./constants";
import {
  buildAdminNotificationsListSearch,
  parseAdminNotificationsListQuery,
} from "../../utilities/adminNotificationsListQuery";

type OfflineApprovalDetails = {
  userId?: string | number;
  username?: string;
  deviceUUID?: string;
  mac?: string;
  loginTimeUtc?: string;
};

const safeParse = (value: string): OfflineApprovalDetails | null => {
  try {
    return JSON.parse(value) as OfflineApprovalDetails;
  } catch {
    console.warn("Invalid JSON in notification detail:", value);
    return null;
  }
};

const formatOfflineApprovalDetailMessage = (raw?: string): string => {
  const text = raw?.trim();
  if (!text) return "No additional details.";

  const parsed = safeParse(text);
  if (parsed) {
    const userId = parsed.userId;
    const username = parsed.username;
    const deviceUUID = parsed.deviceUUID;
    const mac = parsed.mac;
    const rawLoginTime = parsed.loginTimeUtc;
    const loginTime =
      typeof rawLoginTime === "string"
        ? (formatLocalTime(rawLoginTime).trim() || rawLoginTime.trim())
        : "";

    const lines: string[] = [];
    if (userId !== undefined && userId !== null) lines.push(`User ID: ${String(userId)}`);
    if (typeof username === "string" && username.trim()) lines.push(`Username: ${username.trim()}`);
    if (typeof deviceUUID === "string" && deviceUUID.trim()) lines.push(`Device UUID: ${deviceUUID.trim()}`);
    if (typeof mac === "string" && mac.trim()) lines.push(`MAC Address: ${mac.trim()}`);
    if (loginTime) lines.push(`Login Time: ${loginTime}`);
    if (lines.length > 0) return lines.join("\n");
  }
  return "No additional details.";
};

export const NotificationsPage = (
) => {
  const { user } = useAuthState();
  const userRole = user?.role ?? "";
  const { getMethod, putMethod } = useApiService();
  const loaderDispatch = useLoaderDispatch();
  const { loading } = useLoaderState();
  const { setNotificationCount } = useNotificationCount();
  const history = useHistory();
  const location = useLocation();
  const { page } = useMemo(
    () => parseAdminNotificationsListQuery(location.search),
    [location.search]
  );
  const size = 20;
  const [detailModal, setDetailModal] = useState<{
    open: boolean;
    title: string;
    content: string;
  }>({ open: false, title: "", content: "" });

  const getNotificationsApi = async () => {
    startLoading(loaderDispatch);
    let response;
    switch (userRole) {
      case UserRole.Admin:
      case UserRole.Warehouse:
        response = await getMethod(`admin/notifications?page=${page}&size=${size}`);
        break;
    }
    stopLoading(loaderDispatch);
    return response?.data;
  };
  
  const { data } = useQuery(["notifications", page], getNotificationsApi, {
    keepPreviousData: true,
  });

  useEffect(() => {
    if (data?.content) {
      const visibleNotificationIds = data.content.map((notification: NotificationData) => notification.id);
      if (visibleNotificationIds.length > 0) {
        putMethod(`admin/notifications/update`, JSON.stringify(visibleNotificationIds))
        .then(async () =>{
          const response = await getMethod("admin/notifications/unseen-count");
          setNotificationCount(response.data);
        })
      }
    }
  }, [data,putMethod, setNotificationCount]);

  const totalPages = data?.totalPages || 1;
  const handlePageChange = (newPage: number) => {
    const nextSearch = buildAdminNotificationsListSearch({ page: newPage });
    if (nextSearch === location.search) {
      return;
    }
    history.push({
      pathname: location.pathname,
      search: nextSearch,
    });
  };
  const getHeaderTitles = () => {
    let headerTitles: string[] = ["Number", "Created At", "Notification Type", "User", "Creator"];
    return headerTitles;
  };

  const notifications = data?.content || [];
  const rows: Array<TableRowProps> = notifications.length
    ? createNotificationsRows(notifications, {
        history,
        onOfflineDeviceDetailClick: (entry) => {
          const prettyDetails =
            entry.notificationType === OFFLINE_DEVICE_APPROVAL_PENDING_LABEL
              ? formatOfflineApprovalDetailMessage(entry.detailMessage)
              : entry.detailMessage?.trim() || "No additional details.";
          setDetailModal({
            open: true,
            title: entry.notificationType,
            content: prettyDetails,
          });
        },
      })
    : [];

  return loading ? (
    <Loader />
  ) : (
    <div >
    <TableOrganism header={createHeaderRow(getHeaderTitles())} rows={rows} />
    <ModalWithText
      open={detailModal.open}
      onClose={() => setDetailModal((s) => ({ ...s, open: false }))}
      title={detailModal.title}
      content={detailModal.content}
    />
    <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: "20px" }} >
    <Pagination 
      count={totalPages} 
      page={page + 1} 
      onChange={(event, newPage) => handlePageChange(newPage - 1)} 
    />
    </div>
  </div>
  );
};
